import { NgStyle } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ChatbotConfig, SpfBucketFileFrontend } from '@shared/index';
import { getContrastColorSafe } from '@shared-angular';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { BotQuickinfoComponent } from '../../../shared/components/ai-bot/utils/bot-quickinfo/bot-quickinfo.component';
import { SharetokenService } from '../../../shared/services/sharetoken.service';
import { BotImportApiResponse, BotImportFailureReport } from '@shared-angular/services/bot-import.types';

export type ShareBot = {
  name: string;
  icon: string;
  color: string;
};

@Component({
  templateUrl: './import.component.html',
  styleUrl: './import.component.scss',
  imports: [RouterLink, NgStyle, BotQuickinfoComponent, ButtonModule, DialogModule]
})
export class ImportComponent implements OnInit {
  private shareToken: string = '';
  private readonly _invalidToken = signal(false);
  public get invalidToken(): boolean { return this._invalidToken(); }
  public set invalidToken(value: boolean) { this._invalidToken.set(value); }
  private readonly _botConfig = signal<ChatbotConfig | undefined>(undefined);
  public get botConfig(): ChatbotConfig | undefined { return this._botConfig(); }
  public set botConfig(value: ChatbotConfig | undefined) { this._botConfig.set(value); }
  private readonly _botDocs = signal<SpfBucketFileFrontend[]>([]);
  public get botDocs(): SpfBucketFileFrontend[] { return this._botDocs(); }
  public set botDocs(value: SpfBucketFileFrontend[]) { this._botDocs.set(value); }
  private readonly _sharedBot = signal<ShareBot>({ name: '', icon: '', color: '' });
  public get sharedBot(): ShareBot { return this._sharedBot(); }
  public set sharedBot(value: ShareBot) { this._sharedBot.set(value); }
  private readonly _importing = signal(false);
  public get importing(): boolean { return this._importing(); }
  public set importing(value: boolean) { this._importing.set(value); }
  private readonly _importErrorDialogVisible = signal(false);
  public get importErrorDialogVisible(): boolean { return this._importErrorDialogVisible(); }
  public set importErrorDialogVisible(value: boolean) { this._importErrorDialogVisible.set(value); }
  private readonly _importFailureMessage = signal('');
  public get importFailureMessage(): string { return this._importFailureMessage(); }
  public set importFailureMessage(value: string) { this._importFailureMessage.set(value); }
  private readonly _importFailureReport = signal<BotImportFailureReport | null>(null);
  public get importFailureReport(): BotImportFailureReport | null { return this._importFailureReport(); }
  public set importFailureReport(value: BotImportFailureReport | null) { this._importFailureReport.set(value); }

  public getContrastColor = getContrastColorSafe;

  constructor(
    private route: ActivatedRoute,
    private SharetokenService: SharetokenService,
    private toastr: ToastrService,
    private router: Router
  ) { }

  public async ngOnInit(): Promise<void> {
    this.shareToken = this.route.snapshot.params['token'] as string;
    await this.getShareTokenInfo(this.shareToken);
  }

  public async importBot() {
    this.importing = true;
    const res = await this.SharetokenService.ImportBot(this.shareToken);
    if (res.status === 'ok') {
      this.toastr.success("IMPORTED");
      await this.router.navigate(['/bot-admin/']);
    } else {
      this.showImportError(res);
    }
    this.importing = false;
  }

  public hasImportReportContent(): boolean {
    if (!this.importFailureReport) {
      return false;
    }
    return this.importFailureReport.errors.length > 0 || this.importFailureReport.cleanupWarnings.length > 0;
  }

  private showImportError(response: BotImportApiResponse): void {
    const message = response.message ?? $localize`:@@import-aborted-generic:Der Import wurde abgebrochen. Es wurden keine Änderungen gespeichert.`;
    this.toastr.error('IMPORTED', message);
    this.importFailureMessage = message;
    this.importFailureReport = response.report ?? null;
    this.importErrorDialogVisible = this.hasImportReportContent();
  }

  public async getShareTokenInfo(token: string) {
    const config = await this.SharetokenService.getShareTokenInfo(token);
    if (config) {
      this.botConfig = config.botconfig;
      this.botDocs = config.docs;
      this.sharedBot = {
        name: config.botconfig.name,
        icon: config.botconfig.icon ?? "",
        color: config.botconfig.color
      };
    } else {
      this.invalidToken = true;
    }
  }
}
